// Assessment thresholds configuration page (FRMW-006).
// T3 config screen — days before each assessment type becomes overdue.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { assessmentThresholdsApi } from '@/lib/api/frameworkConfig'
import {
  assessmentThresholdsSchema,
  type AssessmentThresholdsFormData,
} from '@/lib/schemas/frameworkConfig'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { ApiRequestError } from '@/lib/api/client'

export function AssessmentThresholdsPage() {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const thresholdsApi = assessmentThresholdsApi(api)

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.frameworkConfig.assessmentThresholds(slug),
    queryFn: () => thresholdsApi.get(),
    retry: (failureCount, err) => {
      if (err instanceof ApiRequestError && err.status === 404) return false
      return failureCount < 3
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<AssessmentThresholdsFormData>({
    resolver: zodResolver(assessmentThresholdsSchema),
    defaultValues: {
      self_assessed_design_days: 365,
      self_assessed_operating_days: 365,
      independent_design_days: 365,
      independent_operating_days: 365,
    },
  })

  // Populate form when data loads
  useEffect(() => {
    if (data) {
      reset({
        self_assessed_design_days: data.self_assessed_design_days,
        self_assessed_operating_days: data.self_assessed_operating_days,
        independent_design_days: data.independent_design_days,
        independent_operating_days: data.independent_operating_days,
      })
    }
  }, [data, reset])

  const mutation = useMutation({
    mutationFn: (formData: AssessmentThresholdsFormData) => thresholdsApi.update(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.frameworkConfig.assessmentThresholds(slug),
      })
      toast({ title: t('assessmentThresholds.updated') })
    },
    onError: (err) => {
      toast({
        title: t('assessmentThresholds.updateFailed'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  function onSubmit(formData: AssessmentThresholdsFormData): void {
    mutation.mutate(formData)
  }

  if (isLoading) {
    return <FormSkeleton />
  }

  if (error && !(error instanceof ApiRequestError && error.status === 404)) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('assessmentThresholds.title') })}
        technicalDetail={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('assessmentThresholds.title')}</h1>
        <p className="text-muted-foreground">{t('assessmentThresholds.description')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('assessmentThresholds.selfAssessed')}</CardTitle>
          <CardDescription>{t('assessmentThresholds.selfAssessedDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="self_assessed_design_days">
                  {t('assessmentThresholds.designLabel')}
                </Label>
                <Input
                  id="self_assessed_design_days"
                  type="number"
                  min={1}
                  {...register('self_assessed_design_days', { valueAsNumber: true })}
                />
                {errors.self_assessed_design_days && (
                  <p className="text-sm text-destructive">
                    {errors.self_assessed_design_days.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="self_assessed_operating_days">
                  {t('assessmentThresholds.operatingLabel')}
                </Label>
                <Input
                  id="self_assessed_operating_days"
                  type="number"
                  min={1}
                  {...register('self_assessed_operating_days', { valueAsNumber: true })}
                />
                {errors.self_assessed_operating_days && (
                  <p className="text-sm text-destructive">
                    {errors.self_assessed_operating_days.message}
                  </p>
                )}
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="mb-1 text-sm font-semibold">
                {t('assessmentThresholds.independent')}
              </h3>
              <p className="mb-4 text-sm text-muted-foreground">
                {t('assessmentThresholds.independentDescription')}
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="independent_design_days">
                    {t('assessmentThresholds.designLabel')}
                  </Label>
                  <Input
                    id="independent_design_days"
                    type="number"
                    min={1}
                    {...register('independent_design_days', { valueAsNumber: true })}
                  />
                  {errors.independent_design_days && (
                    <p className="text-sm text-destructive">
                      {errors.independent_design_days.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="independent_operating_days">
                    {t('assessmentThresholds.operatingLabel')}
                  </Label>
                  <Input
                    id="independent_operating_days"
                    type="number"
                    min={1}
                    {...register('independent_operating_days', { valueAsNumber: true })}
                  />
                  {errors.independent_operating_days && (
                    <p className="text-sm text-destructive">
                      {errors.independent_operating_days.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-2">
              <Button type="submit" disabled={isSubmitting || !isDirty}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common:saving')}
                  </>
                ) : (
                  t('common:save')
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
